import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../utils/AuthContext';
import { UserCircleIcon, SignOutIcon, UserIcon } from '../../utils/IconWrapper';
import { useLanguage } from '../../i18n/LanguageContext';

const UserMenuContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const UserButton = styled(motion.button)`
  background: none;
  border: none;
  color: var(--text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: var(--accent-primary);
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent-primary), var(--accent-secondary));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.8rem;
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  background: var(--bg-secondary);
  border: 2px solid var(--border-color);
  border-radius: 12px;
  padding: 1rem 0;
  min-width: 220px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  
  &::before {
    content: '';
    position: absolute;
    top: -8px;
    right: 16px;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid var(--border-color);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -6px;
    right: 17px;
    width: 0;
    height: 0;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-bottom: 7px solid var(--bg-secondary);
  }
`;

const MenuItem = styled(motion.div)`
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  &:hover {
    background-color: var(--bg-primary);
  }
  
  a {
    color: var(--text-primary);
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    
    &:hover {
      color: var(--accent-primary);
    }
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background-color: var(--border-color);
  margin: 0.5rem 0;
`;

const UserInfo = styled.div`
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 0.5rem;
  
  .user-name {
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 0.25rem;
  }
  
  .user-email {
    font-size: 0.8rem;
    color: var(--text-secondary);
  }
`;

const LogoutButton = styled(motion.button)`
  background: none;
  border: none;
  color: var(--text-primary);
  cursor: pointer;
  width: 100%;
  text-align: left;
  padding: 0.75rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }
`;

const UserMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, profile, logout, loading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  // Chiudi il menu cliccando fuori
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    setIsOpen(false);
    navigate('/');
  };

  const getUserInitials = () => {
    if (profile?.full_name) {
      return profile.full_name
        .split(' ')
        .map(name => name.charAt(0))
        .slice(0, 2)
        .join('')
        .toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const toggleMenu = () => setIsOpen(!isOpen);

  if (loading) {
    return (
      <UserMenuContainer>
        <UserButton>
          <div style={{ width: 32, height: 32, opacity: 0.5 }}>
            <UserCircleIcon size={32} />
          </div>
        </UserButton>
      </UserMenuContainer>
    );
  }

  return (
    <UserMenuContainer ref={menuRef}>
      <UserButton
        onClick={toggleMenu}
        whileTap={{ scale: 0.95 }}
      >
        {user ? (
          <UserAvatar>
            {getUserInitials()}
          </UserAvatar>
        ) : (
          <UserCircleIcon size={32} />
        )}
      </UserButton>

      <AnimatePresence>
        {isOpen && (
          <DropdownMenu
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {user ? (
              // Menu per utente autenticato
              <>
                <UserInfo>
                  <div className="user-name">
                    {profile?.full_name || 'Utente'}
                  </div>
                  <div className="user-email">
                    {user.email}
                  </div>
                </UserInfo>

                <MenuItem
                  whileHover={{ x: 5 }}
                  onClick={() => setIsOpen(false)}
                >
                  <Link to="/dashboard">
                    <UserIcon size={16} />
                    Dashboard
                  </Link>
                </MenuItem>

                <MenuDivider />

                <LogoutButton
                  onClick={handleLogout}
                  whileHover={{ x: 5 }}
                >
                  <SignOutIcon size={16} />
                  Logout
                </LogoutButton>
              </>
            ) : (
              // Menu per utente non autenticato
              <>
                <MenuItem
                  whileHover={{ x: 5 }}
                  onClick={() => setIsOpen(false)}
                >
                  <Link to="/register">
                    <UserIcon size={16} />
                    Registrati
                  </Link>
                </MenuItem>

                <MenuItem
                  whileHover={{ x: 5 }}
                  onClick={() => setIsOpen(false)}
                >
                  <Link to="/login">
                    <SignOutIcon size={16} />
                    Accedi
                  </Link>
                </MenuItem>
              </>
            )}
          </DropdownMenu>
        )}
      </AnimatePresence>
    </UserMenuContainer>
  );
};

export default UserMenu; 